/* eslint-disable @typescript-eslint/no-unused-vars */
import { useLazyQuery } from '@apollo/client'
import { Button, Collapse, Hidden } from '@material-ui/core'
import FilterListIcon from '@material-ui/icons/FilterList'
import { Pagination } from '@material-ui/lab'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import PageHeader from '../../components/PageHeader'
import { TRANSACTIONS_QUERY } from '../../graphql/walletTransactions'
import { getTranslate } from '../../localization'
import { SortBy } from '../../types/walletEnums'
import {
  TransactionFilterInput,
  TransactionResponse
} from '../../types/walletTransaction'
import { TransactionHistoryFilterBox } from './TransactionHistoryFilters'
import { WalletTransactionListTableDesktop } from './WalletTransactionListTableDesktop'
import WalletTransactionSupportMobile from './WalletTransactionSupportMobile'
import useStyles from './styles/index.styles'

type Props = {}

export default function WalletTransactionHistory({}: Props) {
  const classes = useStyles()
  const params = useParams<{ walletId: string }>()

  const [filterButtonSelected, setFilterButtonSelected] =
    useState<boolean>(false)
  const perPage = 6
  const [page, setpage] = useState<number>(1)
  const [filters, setfilters] = useState<TransactionFilterInput>({
    walletID: params.walletId,
    page: page,
    perPage: perPage
  })
  const [sortBy, setsortBy] = useState<SortBy>('dectime')
  const [
    getTransactions,
    {
      data: transactionsData,
      loading: loadingForTransactions,
      refetch,
      error: transactionsError,
      variables: transactionVariables
    }
  ] = useLazyQuery<{
    transactions: TransactionResponse
  }>(TRANSACTIONS_QUERY, {
    notifyOnNetworkStatusChange: true
  })
  useEffect(() => {
    const currentFilters = {
      ...filters,
      walletID: params.walletId,
      ...(filters.walletID !== params.walletId ? { page: 1 } : {})
    }
    setfilters(currentFilters)
    if (filters.walletID !== params.walletId) setpage(1)
    getTransactions({
      variables: {
        filters: currentFilters,
        sortBy: sortBy
      }
    })
  }, [sortBy, params.walletId])

  const supportAvailable =
    !loadingForTransactions &&
    !transactionsError &&
    transactionVariables?.filters?.walletID === params.walletId

  return (
    <div>
      <PageHeader title={getTranslate('Transactions History')} />
      <Hidden smDown>
        <div className={classes.filterButtonContainer}>
          <Button
            size="small"
            aria-expanded={filterButtonSelected}
            aria-controls="wallet-transaction-filters"
            startIcon={<FilterListIcon />}
            variant="outlined"
            color="primary"
            onClick={() => setFilterButtonSelected(!filterButtonSelected)}>
            {getTranslate('Filter')}
          </Button>
        </div>
      </Hidden>
      <Hidden smDown>
        <div id="wallet-transaction-filters">
          <Collapse in={filterButtonSelected}>
            <TransactionHistoryFilterBox
              filters={filters}
              setfilters={setfilters}
              setPage={setpage}
              getTransactions={getTransactions}
              loadingForTransactions={loadingForTransactions}
              sortBy={sortBy}
            />
          </Collapse>
        </div>
      </Hidden>
      <Hidden smDown>
        <WalletTransactionListTableDesktop
          supportAvailable={supportAvailable}
          data={transactionsData?.transactions.transactions || []}
          setSort={setsortBy}
          sort={sortBy}
          {...{
            refetch,
            setpage,
            loadingForTransactions
          }}
        />
      </Hidden>
      <Hidden mdUp>
        <WalletTransactionSupportMobile
          data={transactionsData?.transactions.transactions || []}
          loading={loadingForTransactions}
          failed={Boolean(transactionsError)}
          available={supportAvailable}
          onRetry={() => {
            getTransactions({
              variables: {
                filters: { ...filters, walletID: params.walletId },
                sortBy
              }
            })
          }}
        />
      </Hidden>
      <div className={classes.paginationContainer}>
        <Pagination
          count={
            transactionsData &&
            (transactionsData.transactions.total % perPage === 0
              ? transactionsData.transactions.total / perPage
              : Math.floor(transactionsData.transactions.total / perPage) + 1)
          }
          color="primary"
          page={page}
          onChange={(_, value) => {
            setpage(value)
            const newFilters = { ...filters, page: value }
            setfilters(newFilters)
            getTransactions({
              variables: {
                filters: newFilters,
                sortBy: sortBy
              }
            })
          }}
        />
      </div>
    </div>
  )
}
